// src/components/pages/dialogs/MoveAppointmentDialog.tsx

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from 'react-i18next';

import { moverCita } from '@/components/hooks/useCitas';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  citaId: string;
  cita: any;
  initialDate?: string;
  initialTime?: string;
  initialObservaciones?: string;
  initialHoraFin?: string;
  duracionMinutos: number;
}

const calcularHoraFin = (hora: string, duracion: number): string => {
  if (!hora || !duracion) return '';
  const [h, m] = hora.split(':').map(Number);
  const inicio = new Date();
  inicio.setHours(h, m, 0, 0);
  const fin = new Date(inicio.getTime() + duracion * 60000);
  return fin.toTimeString().slice(0, 5);
};

const MoveAppointmentDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  citaId,
  initialDate = '',
  initialTime = '',
  initialObservaciones = '',
  initialHoraFin = '',
  duracionMinutos,
}) => {
  const { t } = useTranslation();

  const [fecha, setFecha] = useState(initialDate);
  const [hora, setHora] = useState(initialTime);
  const [horaFin, setHoraFin] = useState(initialHoraFin);
  const [observaciones, setObservaciones] = useState(initialObservaciones);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (hora && duracionMinutos) {
      const nuevaFin = calcularHoraFin(hora, duracionMinutos);
      setHoraFin(nuevaFin);
    }
  }, [hora, duracionMinutos]);

  if (!open) return null;

  const handleSave = async () => {
    if (!fecha || !hora) {
      toast.error(t('moveAppointmentDialog.errorNoDateTime'));
      return;
    }

    if (fecha === initialDate && hora === initialTime && horaFin === initialHoraFin) {
      onClose();
      return;
    }

    setLoading(true);
    const res = await moverCita(citaId, fecha, hora, horaFin, 'trabajador');
    setLoading(false);

    if (!res.ok) {
      toast.error(t('moveAppointmentDialog.errorSave'));
      return;
    }

    toast.success(t('moveAppointmentDialog.successSave'));
    onSuccess();
  };

  return (
    <>
      <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 mt-1 space-y-5 text-sm text-gray-700 dark:text-gray-300">
        <div>
          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
            {t('moveAppointmentDialog.labelDate')}
          </label>
          <Input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="h-10 text-sm rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
          />
        </div>

        <div>
          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
            {t('moveAppointmentDialog.labelTimeStart')}
          </label>
          <Input
            type="time"
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            className="h-10 text-sm rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
          />
        </div>

        <div>
          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
            {t('moveAppointmentDialog.labelTimeEnd')}
          </label>
          <Input
            type="time"
            value={horaFin}
            onChange={(e) => setHoraFin(e.target.value)}
            className="h-10 text-sm rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
          />
        </div>

        <div>
          <label className="block font-medium mb-1 text-gray-700 dark:text-gray-300">
            {t('moveAppointmentDialog.labelNotes')}
          </label>
          <Textarea
            rows={3}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder={t('moveAppointmentDialog.notesPlaceholder')}
            className="resize-none rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>

      <DialogFooter className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3">
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outline"
          className="w-full sm:w-auto rounded-lg font-medium bg-transparent dark:bg-transparent border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          {t('moveAppointmentDialog.cancel')}
        </Button>

        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full sm:w-auto rounded-xl text-white font-semibold text-base shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] py-3 disabled:opacity-50 transition-colors"
        >
          {loading
            ? t('moveAppointmentDialog.saving')
            : t('moveAppointmentDialog.saveBtn')}
        </Button>
      </DialogFooter>
    </>
  );
};

export default MoveAppointmentDialog;